import React, { useState, useEffect } from 'react';
import masaLogo from '../Assets/MasaLogo.png';
import styles from './RequestsManagement.module.css';
import { getAllRequests, updateRequestState, deleteRequest } from '../api/requestApi';
import { addProductQuantity, removeProductQuantity } from '../api/productApi';

const RequestsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    getAllRequests()
      .then((response) => setRequests(response.data))
      .catch((error) => {
        console.error('Error fetching requests:', error);
        setErrorMessage('Failed to fetch requests. Please try again.');
      });
  }, []);

  const handleAccept = async (id) => {
    try {
      const request = requests.find((req) => req._id === id);
      if (!request) {
        setErrorMessage(`Request with ID ${id} not found.`);
        return;
      }

      const payload = { sortName: request.sortName, color: request.color, quantity: request.quantity };
      if (request.action === 'add') {
        await addProductQuantity(request.productSKU, payload);
      } else if (request.action === 'remove') {
        await removeProductQuantity(request.productSKU, payload);
      }

      await updateRequestState(id, 'Accepted');
      setModalMessage(`Request ${id} has been accepted and processed.`);
      setShowModal(true);

      setRequests((prev) =>
        prev.map((req) =>
          req._id === id ? { ...req, state: 'Accepted' } : req
        )
      );
    } catch (error) {
      console.error('Error in handleAccept:', error);
      setErrorMessage('Failed to accept the request. Please try again.');
    }
  };

  const handleReject = async (id) => {
    try {
      await updateRequestState(id, 'Rejected');
      setModalMessage(`Request ${id} has been rejected.`);
      setShowModal(true);

      setRequests((prev) =>
        prev.map((req) =>
          req._id === id ? { ...req, state: 'Rejected' } : req
        )
      );
    } catch (error) {
      console.error('Error rejecting request:', error);
      setErrorMessage('Failed to reject the request. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRequest(id);
      setModalMessage(`Request ${id} has been deleted.`);
      setShowModal(true);

      setRequests((prev) => prev.filter((req) => req._id !== id));
    } catch (error) {
      console.error('Error deleting request:', error);
      setErrorMessage('Failed to delete the request. Please try again.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <main className={styles.requestsManagement}>
      <header className={styles.header}>
        <img
          loading="lazy"
          src={masaLogo}
          className={styles.logo}
          alt="Dashboard Logo"
        />
        <h1 className={styles.title}>Requests Management</h1>
      </header>
      <div className={styles.explanationText}>
        <p>Below are the requests from the workers. You can choose to accept, reject, or delete each request.</p>
      </div>
      {errorMessage && (
        <div className={styles.errorBanner}>{errorMessage}</div>
      )}
      <section className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Requester Username</th>
              <th>Product Name</th>
              <th>SKU</th>
              <th>Quantity</th>
              <th>Color</th>
              <th>Sort Name</th>
              <th>Action</th>
              <th>State</th>
              <th>Request Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request._id}>
                <td>{request.requestId}</td>
                <td>{request.requesterUsername || 'N/A'}</td>
                <td>{request.productName}</td>
                <td>{request.productSKU}</td>
                <td>{request.quantity}</td>
                <td>{request.color}</td>
                <td>{request.sortName}</td>
                <td>{request.action}</td>
                <td>{request.state}</td>
                <td>{new Date(request.date).toLocaleDateString()}</td>
                <td>
                  <button
                    className={styles.acceptButton}
                    onClick={() => handleAccept(request._id)}
                    disabled={request.state !== 'Null'}
                  >
                    Accept
                  </button>
                  <button
                    className={styles.rejectButton}
                    onClick={() => handleReject(request._id)}
                    disabled={request.state !== 'Null'}
                  >
                    Reject
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDelete(request._id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {showModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <p>{modalMessage}</p>
            <button className={styles.closeButton} onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default RequestsManagement;
